import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { optionApi } from '@app/store';
import { IOption, OptionDTO } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { OptionForm } from './OptionForm';
import { OptionFormValues, transformToUpdatableFormOption } from './option-form.helpers';
import { useToast, useTriptych, useUpdateCardData } from '@app/hooks';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { valeursDeCopieDeLOption } from '../Duplication/valeurs-de-copie';

interface OptionUpdateProps {
  option?: IOption;
  isFetching?: boolean;
  onDelete: (product: Partial<IOption>) => void;
}

export const OptionUpdate = ({ option, isFetching, onDelete }: OptionUpdateProps) => {
  const toast = useToast();

  const triptych = useTriptych()

  const [update, { isLoading, error: apiErrors }] = optionApi.useUpdateOneOptionMutation();

  const formMethods = useForm<OptionFormValues>({
    resolver: classValidatorResolver(OptionDTO),
    defaultValues: transformToUpdatableFormOption(option),
  });

  // @ts-ignore
  useUpdateCardData(formMethods, ['name']);

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormOption(option));
  }, [option, formMethods.reset]);

  const onUpdate = async (formValues: OptionFormValues) => {
    try {
      const updatedOption = await update({ _id: option?._id, ...formValues }).unwrap();

      triptych.close({ mode: 'UPDATE', data: updatedOption });

      toast.success('Votre option a bien été modifiée.')
    } catch {
      toast.error('Une erreur est survenue lors de la modification de votre option.')
    }
  }

  return (
    <Panel.Content>

      <Form isLoading={isFetching} formMethods={formMethods} apiErrors={apiErrors}>

        <Panel.FormContainer>

          <Panel.Header>
            <CheckPermissions permissions={['dashboard::options.create']}>
              <Button
                title="Dupliquer"
                disabled={!option || isFetching}
                bordered
                onPress={() => triptych.open({ mode: 'CREATE', data: valeursDeCopieDeLOption(option as IOption) })} />
            </CheckPermissions>

            <CheckPermissions permissions={['dashboard::options.delete']}>
              <Button
                title="Supprimer"
                onPress={() => onDelete(option!)}
                bordered
                important />
            </CheckPermissions>
          </Panel.Header>

          <OptionForm />

        </Panel.FormContainer>

        <Panel.Actions>
          <Button
            title="Retour"
            grow
            large
            bordered
            onPress={() => triptych.close()} />

          <Button
            loading={isLoading}
            onPress={formMethods.handleSubmit(onUpdate)}
            title="Valider"
            grow
            large />
        </Panel.Actions>

      </Form>

    </Panel.Content>
  );
};
